import { IProductCustomization } from "@faire/web-api/indigofair/data/IProductCustomization";
import { IProductOption as IFullProductOption } from "@faire/web-api/indigofair/data/IProductOption";
import { IQuickAdd } from "@faire/web-api/indigofair/data/IQuickAdd";
import { StrictLocalizeFunction } from "@faire/web/common/localization";

import {
  isPrePackProduct,
  isPrePackProductOption,
} from "../../features/PrePacks/isPrePackProduct";

export namespace LegacyIQuickAddConverter {
  export interface IProduct {
    options: IProductOption[];
    customizations: IProductCustomization[];
    min_order_quantity: number | undefined;
    per_style_min_order_quantity: number | undefined;
    allow_sales_when_out_of_stock: boolean | undefined;
    has_pre_packs_available: boolean | undefined;
    is_locked_view: boolean | undefined;
  }

  export type IProductOption = Pick<
    IFullProductOption,
    "token" | "unit_multiplier" | "wholesale_price" | "available_units" | "type"
  >;
}

/**
 * Legacy converter to extract quick add data from product.
 *
 * There should be no new uses of this. Instead server should implement this logic and pass quick add data to the client.
 */
export class LegacyIQuickAddConverter {
  private readonly product: LegacyIQuickAddConverter.IProduct;
  private readonly options: LegacyIQuickAddConverter.IProductOption[];

  /**
   * @param product The product.
   * @param options Product options. This would be different from product.options only for product line collections
   *   where a product is represented with multiple tiles, one for each option, so options would be one of product
   *   options.
   */
  constructor(
    product: LegacyIQuickAddConverter.IProduct,
    private strictLocalize: StrictLocalizeFunction
  ) {
    this.product = product;
    this.options = product.options;
  }

  // Disabling because unit tests spy on the prototype
  // eslint-disable-next-line @faire/prefer-arrow-methods
  toIQuickAdd(): IQuickAdd | undefined {
    if (this.product.is_locked_view) {
      return undefined;
    }

    return IQuickAdd.build({
      quick_add_text: this.getQuickAddText(),
      quick_add_option:
        this.options.length === 1 &&
        !this.getProductHasRequiredCustomization() &&
        this.options[0]
          ? this.getQuickAddOption(this.options[0])
          : undefined,
    });
  }

  private getProductHasRequiredCustomization = (): boolean =>
    this.product?.customizations?.some(
      ({ required, active }) => !!required && !!active
    );

  private getQuickAddText = (): string => {
    const chooseOptionsLocalization = this.strictLocalize({
      defaultMessage: "Choose Options",
      description: { text: "Click to choose from various product options" },
    });
    if (this.options.length > 1) {
      return chooseOptionsLocalization;
    }
    if (this.getProductHasRequiredCustomization()) {
      return this.strictLocalize({
        defaultMessage: "Add Customization",
        description: { text: "Click to add customizations to this product" },
      });
    }
    if (isPrePackProduct(this.product)) {
      return this.strictLocalize({
        defaultMessage: "+ Quick Add Pack",
        description: { text: "Click to quick add the prepack option to cart" },
      });
    }
    // placing this after prepack because of precedence
    if (this.product.per_style_min_order_quantity) {
      return chooseOptionsLocalization;
    }
    return this.strictLocalize({
      defaultMessage: "+ Quick Add Case",
      description: {
        text: "Click to quick add the only product option to cart",
      },
    });
  };

  private getQuickAddOption = (
    option: LegacyIQuickAddConverter.IProductOption
  ): IQuickAdd.IQuickAddOption =>
    IQuickAdd.IQuickAddOption.build({
      option_token: option.token,
      option_unit_multiplier: option.unit_multiplier,
      option_min_order_quantity: isPrePackProductOption(option)
        ? 1
        : this.product.min_order_quantity,
      option_available_units: this.product.allow_sales_when_out_of_stock
        ? undefined
        : option.available_units,
    });
}
